import {
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import { Icon, type IconName } from '@luacentrix/icons';
import { cn, variant } from './utils';
import { place } from './floating';

export interface CheckboxProps {
  checked: boolean;
  indeterminate?: boolean;
  disabled?: boolean;
  error?: boolean;
  label?: ReactNode;
  description?: ReactNode;
  ariaLabel?: string;
  name?: string;
  value?: string;
  onChange: (next: boolean) => void;
  id?: string;
}

export function Checkbox({
  checked,
  indeterminate = false,
  disabled = false,
  error = false,
  label,
  description,
  ariaLabel,
  name,
  value,
  onChange,
  id,
}: CheckboxProps) {
  const generated = useId();
  const inputId = id ?? generated;
  const descriptionId = description ? `${inputId}-description` : undefined;
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (element !== null) {
      element.indeterminate = indeterminate && !checked;
    }
  }, [indeterminate, checked]);

  const hasVisibleLabel = label !== undefined || description !== undefined;

  const control = (
    <input
      ref={ref}
      id={inputId}
      name={name}
      value={value}
      type="checkbox"
      className="lc-checkbox__control"
      checked={checked}
      disabled={disabled}
      aria-checked={indeterminate && !checked ? 'mixed' : checked}
      aria-invalid={error || undefined}
      aria-label={hasVisibleLabel ? undefined : ariaLabel}
      aria-describedby={descriptionId}
      onChange={event => onChange(event.currentTarget.checked)}
    />
  );

  if (!hasVisibleLabel) {
    return control;
  }

  return (
    <label
      htmlFor={inputId}
      className={cn('lc-checkbox', variant('lc-checkbox', { disabled, error }))}
    >
      {control}
      <span className="lc-checkbox__label">
        {label}
        {description === undefined ? null : (
          <span id={descriptionId} className="lc-field__hint" style={{ display: 'block' }}>
            {description}
          </span>
        )}
      </span>
    </label>
  );
}

export interface ToggleProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  loading?: boolean;
  error?: boolean;
  label?: ReactNode;
  ariaLabel?: string;
  describedBy?: string;
  id?: string;
}

export function Toggle({
  checked,
  onChange,
  disabled = false,
  loading = false,
  error = false,
  label,
  ariaLabel,
  describedBy,
  id,
}: ToggleProps) {
  const generated = useId();
  const toggleId = id ?? generated;

  const button = (
    <button
      type="button"
      id={toggleId}
      role="switch"
      className="lc-toggle__track"
      data-checked={String(checked)}
      aria-checked={checked}
      aria-busy={loading || undefined}
      aria-disabled={disabled || undefined}
      aria-label={label === undefined ? ariaLabel : undefined}
      disabled={disabled}
      aria-describedby={describedBy}
      onClick={() => onChange(!checked)}
    >
      <span className="lc-toggle__thumb" />
    </button>
  );

  if (label === undefined) return button;

  return (
    <span className={cn('lc-toggle', variant('lc-toggle', { error }))}>
      {button}
      <label htmlFor={toggleId} className="lc-toggle__label">
        {label}
      </label>
    </span>
  );
}

export interface SelectOption<T extends string = string> {
  value: T;
  label: string;
  disabled?: boolean;
  group?: string;
  description?: string;
}

export interface SelectProps<T extends string = string> {
  options: Array<SelectOption<T>>;
  value: T | undefined;
  onChange: (next: T) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  searchable?: boolean;
  label?: string;
  name?: string;
  id?: string;
  ariaLabel?: string;
  describedBy?: string;
}

export function Select<T extends string = string>({
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  disabled = false,
  error = false,
  searchable = false,
  label,
  name,
  id,
  ariaLabel,
  describedBy,
}: SelectProps<T>) {
  const generated = useId();
  const selectId = id ?? generated;
  const listboxId = `${selectId}-listbox`;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const [position, setPosition] = useState({ top: 0, left: 0, maxHeight: 240, width: 180 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const visible = options.filter(option => {
    if (!searchable || query.length === 0) return true;
    return option.label.toLowerCase().includes(query.toLowerCase());
  });

  const selectableIndexes = visible
    .map((option, index) => (option.disabled ? -1 : index))
    .filter(index => index >= 0);

  const selected = options.find(option => option.value === value);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    const initial = options.findIndex(option => option.value === value && option.disabled !== true);
    setHighlight(initial >= 0 ? initial : 0);
  }, [open, value, options]);

  useEffect(() => {
    if (!open) return;
    const anchor = triggerRef.current?.getBoundingClientRect();
    if (anchor === undefined) return;
    const measured = place(
      {
        top: anchor.top,
        left: anchor.left,
        width: anchor.width,
        bottom: anchor.bottom,
        right: anchor.right,
      },
      { width: Math.max(anchor.width, 180), height: 240 },
      { width: window.innerWidth, height: window.innerHeight },
    );
    setPosition({
      top: measured.top,
      left: measured.left,
      maxHeight: measured.maxHeight,
      width: Math.max(anchor.width, 180),
    });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (
        triggerRef.current?.contains(target) !== true &&
        panelRef.current?.contains(target) !== true
      ) {
        setOpen(false);
      }
    };
    const onScroll = () => setOpen(false);
    document.addEventListener('pointerdown', onPointerDown, true);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  useEffect(() => {
    if (open && searchable) searchRef.current?.focus();
  }, [open, searchable]);

  const move = (delta: number) => {
    if (selectableIndexes.length === 0) return;
    const current = selectableIndexes.indexOf(highlight);
    const next =
      delta > 0
        ? selectableIndexes[(current + 1 + selectableIndexes.length) % selectableIndexes.length]
        : selectableIndexes[(current - 1 + selectableIndexes.length) % selectableIndexes.length];
    const resolved = next ?? selectableIndexes[0];
    if (resolved !== undefined) setHighlight(resolved);
  };

  const commit = (option: SelectOption<T> | undefined) => {
    if (option === undefined || option.disabled === true) return;
    onChange(option.value);
    setOpen(false);
    triggerRef.current?.focus();
  };

  const onKeyDown = (event: ReactKeyboardEvent<HTMLElement>) => {
    if (disabled) return;
    if (!open) {
      if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        setOpen(true);
      }
      return;
    }
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        move(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        move(-1);
        break;
      case 'Home':
        event.preventDefault();
        setHighlight(selectableIndexes[0] ?? 0);
        break;
      case 'End':
        event.preventDefault();
        setHighlight(selectableIndexes[selectableIndexes.length - 1] ?? 0);
        break;
      case 'Escape':
        event.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        commit(visible[highlight]);
        break;
      default:
        break;
    }
  };

  const grouped: Array<{ group: string | null; items: Array<SelectOption<T>> }> = [];
  for (const option of visible) {
    const last = grouped[grouped.length - 1];
    if (last !== undefined && last.group === (option.group ?? null)) {
      last.items.push(option);
    } else {
      grouped.push({ group: option.group ?? null, items: [option] });
    }
  }

  let flatIndex = -1;

  return (
    <span className="lc-select" style={{ display: 'inline-flex' }}>
      {name === undefined ? null : <input type="hidden" name={name} value={value ?? ''} />}
      {label === undefined ? null : (
        <label className="lc-field__label" htmlFor={selectId}>
          {label}
        </label>
      )}
      <button
        ref={triggerRef}
        type="button"
        id={selectId}
        className="lc-select__trigger"
        role="combobox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-haspopup="listbox"
        aria-activedescendant={open ? `${listboxId}-option-${highlight}` : undefined}
        aria-invalid={error || undefined}
        aria-disabled={disabled || undefined}
        aria-label={label === undefined ? ariaLabel : undefined}
        aria-describedby={describedBy}
        disabled={disabled}
        data-open={String(open)}
        onClick={() => setOpen(current => !current)}
        onKeyDown={onKeyDown}
      >
        <span
          className={cn(
            'lc-select__value',
            selected === undefined && 'lc-select__value--placeholder',
          )}
        >
          {selected?.label ?? placeholder}
        </span>
        <Icon name="chevronDown" size="sm" />
      </button>

      {open ? (
        <div
          ref={panelRef}
          id={listboxId}
          role="listbox"
          aria-label={ariaLabel ?? label}
          className="lc-select__panel"
          style={{
            top: position.top,
            left: position.left,
            width: position.width,
            maxHeight: position.maxHeight,
          }}
          onKeyDown={onKeyDown}
          tabIndex={-1}
        >
          {searchable ? (
            <input
              ref={searchRef}
              className="lc-select__search"
              type="text"
              value={query}
              placeholder="Search"
              aria-label="Search options"
              onChange={event => {
                setQuery(event.currentTarget.value);
                setHighlight(0);
              }}
              onKeyDown={event => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  commit(visible[highlight]);
                }
              }}
            />
          ) : null}

          {visible.length === 0 ? (
            <p className="lc-select__empty">No matching options</p>
          ) : (
            grouped.map(section => (
              <div key={`${section.group ?? 'root'}-${section.items[0]?.value ?? 'empty'}`}>
                {section.group === null ? null : (
                  <p className="lc-select__group-label">{section.group}</p>
                )}
                {section.items.map(option => {
                  flatIndex += 1;
                  const index = flatIndex;
                  return (
                    <button
                      key={option.value}
                      id={`${listboxId}-option-${index}`}
                      type="button"
                      role="option"
                      className="lc-select__option"
                      aria-selected={option.value === value}
                      disabled={option.disabled === true}
                      data-highlighted={String(index === highlight)}
                      data-value={option.value}
                      onPointerEnter={() => {
                        if (option.disabled !== true) setHighlight(index);
                      }}
                      onClick={() => commit(option)}
                    >
                      <span>
                        {option.label}
                        {option.description === undefined ? null : (
                          <span className="lc-field__hint" style={{ display: 'block' }}>
                            {option.description}
                          </span>
                        )}
                      </span>
                      {option.value === value ? <Icon name="check" size="sm" /> : null}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
      ) : null}
    </span>
  );
}

export interface MenuItem {
  id: string;
  label: string;
  icon?: IconName;
  danger?: boolean;
  disabled?: boolean;
  onSelect: () => void;
}

export interface MenuProps {
  items: Array<MenuItem>;
  open: boolean;
  anchor: { top: number; left: number; right: number; bottom: number; width: number };
  onClose: () => void;
  label: string;
}

export function Menu({ items, open, anchor, onClose, label }: MenuProps) {
  const [highlight, setHighlight] = useState(0);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    setHighlight(0);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (panelRef.current?.contains(event.target as Node) !== true) onClose();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose]);

  if (!open) return null;

  const computed = place(
    anchor,
    { width: 200, height: items.length * 34 + 12 },
    { width: window.innerWidth, height: window.innerHeight },
  );

  return (
    <div
      ref={panelRef}
      role="menu"
      aria-label={label}
      className="lc-menu"
      style={{ top: computed.top, left: computed.left, width: 200 }}
    >
      {items.map((item, index) => (
        <button
          key={item.id}
          type="button"
          role="menuitem"
          className={cn('lc-menu__item', item.danger && 'lc-menu__item--danger')}
          disabled={item.disabled === true}
          data-highlighted={String(index === highlight)}
          onPointerEnter={() => setHighlight(index)}
          onClick={() => {
            if (item.disabled === true) return;
            item.onSelect();
            onClose();
          }}
        >
          {item.icon === undefined ? null : <Icon name={item.icon} size="sm" />}
          {item.label}
        </button>
      ))}
    </div>
  );
}
